// F7 / FR-N7, FR-M10a: Admin → Integrations. Cipher, Slack and Teams destinations for meeting
// notes. URLs are write-only: the API returns only `…last4`, so this page can set, replace or
// clear one but never show it. Every destination is opt-in (configured = on) per org; the
// per-series toggles live in Settings → Meetings.
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Badge, Button, Callout, Input, ToggleRow } from "@grackle/ui";
import { SettingsSection } from "./SettingsSection";
import { ApiFail } from "../api/client";
import {
  getIntegrations,
  putIntegrations,
  testIntegration,
  type DestinationChannel,
  type IntegrationsOut,
} from "../notifications/api";
import type { AdminOutletContext } from "./AdminLayout";

interface DestinationCopy {
  channel: DestinationChannel;
  title: string;
  help: string;
  placeholder: string;
  field: "cipher_url" | "slack_url" | "teams_url";
}

const DESTINATIONS: DestinationCopy[] = [
  {
    channel: "cipher",
    title: "Cipher",
    help: "Paste the inbound webhook URL of a Searchable Cipher channel.",
    placeholder: "https://<cipher-host>/api/v1/webhooks/inbound/<token>",
    field: "cipher_url",
  },
  {
    channel: "slack",
    title: "Slack",
    help: "A Slack incoming webhook URL.",
    placeholder: "https://hooks.slack.com/services/…",
    field: "slack_url",
  },
  {
    channel: "teams",
    title: "Microsoft Teams",
    help: "A Teams Workflows webhook URL (“When a Teams webhook request is received”).",
    placeholder: "https://…logic.azure.com/workflows/…",
    field: "teams_url",
  },
];

function DestinationCard({
  copy,
  orgId,
  state,
  onChanged,
}: {
  copy: DestinationCopy;
  orgId: string;
  state: IntegrationsOut;
  onChanged: (next: IntegrationsOut) => void;
}) {
  const dest = state[copy.channel];
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState<"save" | "clear" | "test" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; status_code: number } | null>(null);

  async function save(value: string | null) {
    setBusy(value === null ? "clear" : "save");
    setError(null);
    setTestResult(null);
    try {
      onChanged(await putIntegrations(orgId, { [copy.field]: value }));
      setUrl("");
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save that.");
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    setBusy("test");
    setError(null);
    setTestResult(null);
    try {
      setTestResult(await testIntegration(orgId, copy.channel));
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't send the test message.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingsSection
      title={copy.title}
      description={copy.help}
      action={
        dest.configured ? (
          <Badge tone="success" dot>
            Connected <span className="mono">{dest.masked}</span>
          </Badge>
        ) : (
          <Badge tone="neutral">Not connected</Badge>
        )
      }
    >
      <div className="admin-inline-form">
        <Input
          label={dest.configured ? `Replace ${copy.title} webhook URL` : `${copy.title} webhook URL`}
          type="password"
          autoComplete="off"
          placeholder={copy.placeholder}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Button onClick={() => void save(url.trim())} disabled={!url.trim() || busy !== null}>
          {busy === "save" ? "Saving…" : "Save"}
        </Button>
        {dest.configured ? (
          <>
            <Button variant="secondary" onClick={() => void test()} disabled={busy !== null}>
              {busy === "test" ? "Sending…" : "Send test message"}
            </Button>
            <Button variant="ghost" onClick={() => void save(null)} disabled={busy !== null}>
              Clear
            </Button>
          </>
        ) : null}
      </div>
      {copy.channel === "cipher" && dest.configured ? (
        <ToggleRow
          label="Post meeting notes to Cipher"
          checked={state.cipher.enabled}
          onChange={async (v) => {
            try {
              onChanged(await putIntegrations(orgId, { cipher_enabled: v }));
            } catch (err) {
              setError(err instanceof ApiFail ? err.message : "Couldn't save that.");
            }
          }}
        />
      ) : null}
      {testResult ? (
        <Callout tone={testResult.ok ? "success" : "danger"} icon={testResult.ok ? "check-circle" : "warning-circle"}>
          {testResult.ok
            ? "Test message delivered."
            : `The destination didn't accept the test message${testResult.status_code ? ` (HTTP ${testResult.status_code})` : ""}.`}
        </Callout>
      ) : null}
      {error ? (
        <Callout tone="danger" icon="warning-circle">
          {error}
        </Callout>
      ) : null}
    </SettingsSection>
  );
}

export default function IntegrationsPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [state, setState] = useState<IntegrationsOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getIntegrations(org.id)
      .then(setState)
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load integrations."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!state) {
    return error ? (
      <Callout tone="danger" icon="warning-circle">
        {error}
      </Callout>
    ) : (
      <div className="admin-row-sub">Loading…</div>
    );
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Integrations</h1>
          {/* FR-M10a: every destination is off until connected, and each series opts in separately. */}
          <p>
        Where Grackle posts finished meeting notes. Each destination is off until you connect it, and each meeting
        series opts in separately. Webhook URLs are stored write-only — Grackle only ever shows the last four
        characters.
          </p>
        </div>
      </header>
        {DESTINATIONS.map((d) => (
          <DestinationCard key={d.channel} copy={d} orgId={org.id} state={state} onChanged={setState} />
        ))}
        <SettingsSection title="Content" description="What a destination receives with each finished meeting.">
          <ToggleRow
            label="Include notes text in destinations"
            description="Off by default: destinations get the title, date and a link only. A channel can have a wider audience than the recording itself, so turn this on only if that is acceptable."
            checked={state.include_notes_text}
            onChange={async (v) => {
              try {
                setState(await putIntegrations(org.id, { include_notes_text: v }));
              } catch (err) {
                setError(err instanceof ApiFail ? err.message : "Couldn't save that.");
              }
            }}
          />
          {error ? (
            <Callout tone="danger" icon="warning-circle">
              {error}
            </Callout>
          ) : null}
        </SettingsSection>
    </>
  );
}
